import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import {
  Button,
  CircularProgress,
  Divider,
  Grid,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useNavigate } from "react-router-dom";
import FormDialog from "common/Modal/Modal";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Items } from "assets/SideBarIcons/items.svg";
import BackendPaginationTable from "common/Table/TableWithPagination";
import { ItemsProfileService } from "subModule/src/core/services";
import { DeleteData, Wrapper } from "subModule/src/type";
import Loader from "layout/Loader";
import { useGetItemsService } from "subModule/src/services/items/useItems";
import { ChangeStatusItemsResponse } from "subModule/src/services/items/interface";
import ItemSearchHeaderMenu from "components/Items/ItemSearchHeaderMenu";
import ItemBulkUpdateMenu from "components/Items/ItemBulkUpdateMenu";
import ItemSearchHeader from "components/Items/ItemSearchHeader";
import { CommonProfileService } from "subModule/src/core/services";
import { themes } from "theme/themes";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { selectUserInfo } from "features/user/userSlice";
import DynamicSelect from "common/Select/Select";
import {
  getSortParamFromColumn,
  ITEM_SORT_FIELD_MAP,
  SortOrder,
} from "Helpers/buildApiSortParam";

const ITEM_SORTABLE_COLUMNS = ["itemNameCode", "category"];
interface SearchFormData {
  [key: string]: string | any;
}
export default function ItemsListing() {
  const navigate = useNavigate();
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [statusModal, setStatusModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>(["name", "category"]);
  const [search, setSearch] = useState<any>({});
  const [sort, setSort] = useState<string | undefined>();
  const [sortOrderBy, setSortOrderBy] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [downloading, setDownloading] = useState<boolean>(false);
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    name: "",
    itemCode: "",
    category: null,
  });
  const [row, setRow] = useState<any>({});
  const [rows, setRows] = useState<any>([]);
  const [multiActionModal, setMultiActionModal] = useState(false);
  const [actionType, setActionType] = useState<{
    label: string;
    value: number;
  } | null>(null);
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetItemsService({
    pageNumber: pageNo,
    size: pageSize,
    ...search,
    sort,
  });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const loggedInUserData = useSelector(selectUserInfo);
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const loggedInUserCurrency = loggedInUserData?.business?.currency;
  const categories = [
    { value: 1, label: t("fixedAsset") },
    { value: 2, label: t("inventoryItem") },
    { value: 3, label: t("spotPurchaseItem") },
  ];
  const fetchData = useCallback(() => {
    refetch()
      .then((res: any) => {
        if (!res?.data?.pages[0]?.success)
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.pages[0]?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search, sort]);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.category]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const onEdit = (row: any) => navigate("/part/" + row.id);
  const showRowView = (row: any) => navigate("/part/" + row.id);
  const onDelete = (row: any) => {
    setDeleteModal(true);
    setRow(row);
  };
  const clearSearch = () => {
    setFilter(["name", "category"]);
    setSearchFormData({ name: "", itemCode: "", category: null });
    if (Object.keys(search).length) {
      setSearch({});
    }
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    setFilter(typeof value === "string" ? value.split(",") : value);
    setSearchFormData((prevFormData) =>
      Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key) ? prevFormData[key] : "",
        ]),
      ),
    );
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val = field === "category" ? (value ? value.value : null) : value;
    if (field === "category" && searchFormData.category === val) return;
    setSearchFormData((prevData) => ({
      ...prevData,
      [field]: val,
    }));
    if (field === "category") setLoading(true);
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string> = {};
    Object.entries(searchFormData).forEach(([key, value]) => {
      if (value) {
        if (key === "name") {
          filteredSearch["title"] = value.trim();
        } else {
          filteredSearch[key] =
            typeof value === "string" ? value?.trim() : value;
        }
      }
    });
    setSearch(filteredSearch);
    setPageNo(1);
    setPageSize(pageSize);
  };
  const onChangeStatus = (row: any) => {
    setRow(row);
    setStatusModal(true);
  };
  const handleSortChange = (orderBy: string, order: SortOrder) => {
    const nextSort = getSortParamFromColumn(
      orderBy,
      order,
      ITEM_SORT_FIELD_MAP,
    );
    if (!nextSort) return;
    setSortOrderBy(orderBy);
    setSortOrder(order);
    setSort(nextSort);
    setPageNo(1);
  };
  const actionslist = [
    { label: "edit", onClick: onEdit, icon: <EditIcon /> },
    { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
  ];
  const adjustActions = (actions: any[]) => {
    return checkPrivileges("ROLE_ITEM_UPDATE")
      ? checkPrivileges("ROLE_ITEM_ARCHIVE")
        ? actions
        : actions.filter((action) => action.label !== "delete")
      : checkPrivileges("ROLE_ITEM_ARCHIVE")
        ? actions.filter((action) => action.label !== "edit")
        : [];
  };
  const DownloadParts = () => {
    CommonProfileService.exportEntityWithParams(
      { ...search },
      "items/export/excel",
    )
      .then((res: Blob) => {
        const blob = new Blob([res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Parts.xlsx`);
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
        enqueueSnackbar(<AlertMessages text={t("downloadedSuccessfully")} />, {
          variant: "success",
        });
      })
      .catch((err) => {
        console.error("===", err);
        enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      })
      .finally(() => {
        setDownloading(false);
      });
  };
  const handleMultiAction = (isChecked: boolean, row: any) => {
    setRows((prev: any) =>
      isChecked
        ? [...prev, row].filter(
            (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
          )
        : prev.filter((r: any) => r.id !== row.id),
    );
  };
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/part/create")}
        heading={t("items")}
        icon={<Items height={28} width={28} />}
        btnText={"create"}
        btnType={"create"}
        showCreateBtn={checkPrivileges("ROLE_ITEM_CREATE")}
      />
      {isFetching || loading ? (
        <Loader />
      ) : (
        <BackendPaginationTable
          columns={[
            { id: "multiAction", label: "", numeric: false, width: "46px" },
            {
              id: "itemNameCode",
              label: "nameItemCode",
              numeric: false,
              width: "24%",
            },
            {
              id: "category",
              label: t("category"),
              numeric: false,
              width: "14%",
            },
            {
              id: "itemCost",
              label: "purchaseAverageCost",
              numeric: false,
              width: "14%",
            },
            { id: "partNo", label: "partNo", numeric: false, width: "12%" },
            {
              id: "manufacturerTitle",
              label: "manufacturer",
              numeric: false,
              width: "14%",
            },
            ...(checkPrivileges("ROLE_ITEM_ACTIVE")
              ? [{ id: "active", label: "status", numeric: false, width: "8%" }]
              : []),
            ...(adjustActions(actionslist).length
              ? [
                  {
                    id: "actions",
                    label: "actions",
                    numeric: false,
                    width: "5%",
                  },
                ]
              : []),
          ]}
          data={tableData?.pages[0]?.data?.content || []}
          pageNo={pageNo}
          pageSize={pageSize}
          tableData={tableData?.pages[0]?.data || {}}
          showCheckbox={false}
          showActions={true}
          enableSorting={true}
          serverSideSorting={true}
          sortOrderBy={sortOrderBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          sortableColumnIds={ITEM_SORTABLE_COLUMNS}
          showHeader={true}
          showSearch={true}
          showFilter={true}
          showCreateButton={true}
          actions={adjustActions(actionslist)}
          handleChangePage={handleChangePage}
          handleChangeRows={handleChangeRows}
          onChangeStatus={onChangeStatus}
          rowClick={checkPrivileges("ROLE_ITEM_UPDATE")}
          showRowView={showRowView}
          loadingSearch={false}
          currency={loggedInUserCurrency || ""}
          handleMultiAction={handleMultiAction}
          selectedRows={rows}
          headerComponent={
            <>
              {rows?.length ? (
                <Box
                  className="me-[10px] flex gap-[10px]"
                  sx={{
                    "& .commonSelect .MuiTypography-root": {
                      fontSize: "11px !important",
                      color: "rgba(0, 0, 0, 0.6) !important",
                      marginBottom: "-4px",
                    },
                    "& .dropdown": { height: "13px !important" },
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      width: "150px",
                      overflow: "hidden",
                      fontSize: "12px",
                      "& .css-coi8w6-indicatorContainer, & .css-8q9606-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "18px",
                          height: "18px",
                        },
                    },
                  }}
                >
                  <DynamicSelect
                    label={
                      t("action") +
                      " (" +
                      rows.length +
                      " " +
                      t("selected") +
                      ")"
                    }
                    isClearable
                    options={[
                      { value: 1, label: t("active") },
                      { value: 2, label: t("inactive") },
                      { value: 3, label: t("delete") },
                    ]}
                    onChange={(selectedOption: any) => {
                      if (selectedOption) {
                        setActionType(selectedOption);
                        setMultiActionModal(true);
                      } else setActionType(null);
                    }}
                    value={actionType}
                  />
                  <Divider
                    className="w-[1px] h-[36px] mt-[15px]"
                    sx={{ background: "#DFD2E1" }}
                    orientation="vertical"
                    flexItem
                  />
                </Box>
              ) : null}
              <ItemSearchHeader
                searchFormData={searchFormData}
                handleChangeFilter={handleChangeFilter}
                handleChangeSearch={handleChangeSearch}
                handleSubmitSearch={handleSubmitSearch}
                clearSearch={clearSearch}
                {...{ filter, search, categories }}
              />
              {checkPrivileges("ROLE_ITEM_EXPORT") ||
              checkPrivileges("ROLE_ITEM_IMPORT") ||
              checkPrivileges("ROLE_ITEM_UPDATE") ? (
                <Box className="table-header-actions">
                  {checkPrivileges("ROLE_ITEM_EXPORT") ? (
                    <Button
                      id="import-button"
                      variant="contained"
                      disableElevation
                      onClick={() => {
                        setDownloading(true);
                        DownloadParts();
                      }}
                      className={`bg-white font-Saira font-[600] text-[13px] px-4 py-1 cursor-pointer rounded-[6px]`}
                      sx={{
                        outline: "2px solid",
                        color: themeColors?.secondary,
                        borderColor: themeColors?.secondary,
                        height: 34,
                        minHeight: 34,
                      }}
                      disabled={downloading}
                    >
                      {downloading ? (
                        <CircularProgress
                          sx={{
                            color: themeColors?.secondary,
                            marginRight: "8px",
                          }}
                          size={16}
                        />
                      ) : null}
                      {t("export")}
                    </Button>
                  ) : null}
                  {checkPrivileges("ROLE_ITEM_IMPORT") ? (
                    <ItemSearchHeaderMenu refetch={refetch} />
                  ) : null}
                  {checkPrivileges("ROLE_ITEM_UPDATE") ? (
                    <ItemBulkUpdateMenu
                      refetch={refetch}
                      selectedRows={rows}
                      pageRows={tableData?.pages[0]?.data?.content || []}
                    />
                  ) : null}
                </Box>
              ) : null}
            </>
          }
        />
      )}
      {openDeleteModal && (
        <FormDialog
          open={openDeleteModal}
          onClose={() => setDeleteModal(false)}
          title={t("delete") + " " + t("Item")}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure you want to delete?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setDeleteModal(false);
            setLoading(true);
            ItemsProfileService.deleteItems((row as any).id)
              .then((response: DeleteData) => {
                if (response.success) {
                  setRows((prev: any) =>
                    prev.filter((r: any) => r.id !== row.id),
                  );
                  if (
                    tableData?.pages[0]?.data?.content?.length === 1 &&
                    pageNo > 1
                  )
                    setPageNo(1);
                  else refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("Item") + " " + t("deleted") + " " + t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="Delete"
        />
      )}
      {statusModal && (
        <FormDialog
          open={statusModal}
          onClose={() => setStatusModal(false)}
          title="Change Status"
          // icon={<div><Home/></div>}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t(`Are you sure?`)}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setStatusModal(false);
            setLoading(true);
            ItemsProfileService.changeStatusItems(
              (row as any).id,
              row.active ? "inactive" : "active",
              {},
            )
              .then((response: ChangeStatusItemsResponse) => {
                if (response.success) {
                  refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("status") +
                        " " +
                        t("changed") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="OK"
        />
      )}
      {multiActionModal && (
        <FormDialog
          open={multiActionModal}
          onClose={(event: any, reason: any) => {
            if (event && reason !== "backdropClick") setMultiActionModal(false);
          }}
          title={
            actionType?.value === 3
              ? t("delete") + " " + t("items")
              : t("Change Status") + " " + t("items")
          }
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>
                  {actionType?.value === 3
                    ? t("Are you sure you want to delete?")
                    : t("Are you sure?")}
                </Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setMultiActionModal(false);
            setLoading(true);
            CommonProfileService.multiActions({
              ids: rows.map((r: any) => r.id),
              action: actionType?.value || 0,
              typeName: "items/bulk/action",
            })
              .then((response: Wrapper) => {
                if (response.success) {
                  setRows([]);
                  setActionType(null);
                  refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        actionType?.value === 3
                          ? t("Items") +
                            " " +
                            t("deleted") +
                            " " +
                            t("successfully")
                          : t("statuses") +
                            " " +
                            t("changed") +
                            " " +
                            t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText={actionType?.value === 3 ? t("Delete") : t("OK")}
        />
      )}
    </Box>
  );
}
